import { useEffect, useState, useRef } from 'react';
import api, { formatApiError, API, fileUrl } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { previewPDF } from '../lib/pdf';
import {
  FolderOpen, FileText, Upload, Eye, Download, Trash2, ChevronDown, ChevronRight,
  ShieldCheck, Briefcase, Award, Receipt, Archive,
} from 'lucide-react';

// Unified neutral styling for all folder cards. The icon keeps a *subtle*
// tint so users can still visually parse the folder types at a glance,
// but the card body / borders / typography stay consistent across the
// entire vault (no more rainbow). Iter T.
const FOLDER_META = {
  bgc:                 { label: 'BGC documents',                  icon: ShieldCheck, iconClass: 'text-emerald-600', help: 'Identity & education documents uploaded during onboarding.' },
  employment_letters:  { label: 'Employment letters',             icon: Briefcase,   iconClass: 'text-[#3D4A8E]',   help: 'Offer letter, relieving / experience / termination letters issued by HR.' },
  certifications:      { label: 'Certifications & achievements',  icon: Award,       iconClass: 'text-amber-600',   help: 'Trainings completed, awards, professional certifications.' },
  reimbursements:      { label: 'Reimbursements',                 icon: Receipt,     iconClass: 'text-sky-600',     help: 'Invoices uploaded against reimbursement claims.' },
  miscellaneous:       { label: 'Miscellaneous',                  icon: Archive,     iconClass: 'text-slate-500',   help: 'Anything else — HR uploads or employee uploads.' },
};

function fmt(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
}

/**
 * Documents vault — used on EmployeeDetail (HR view) AND on the employee's own /documents page.
 * Sync is automatic because every folder reads the same backend collections both sides write to.
 */
export default function EmployeeDocumentsVault({ employeeId, canManage = false, isSelf = false }) {
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({ bgc: true, employment_letters: true, certifications: true, reimbursements: true, miscellaneous: true });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  /* eslint-disable */
  useEffect(() => { if (employeeId) load(); }, [employeeId]);
  /* eslint-enable */

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/documents-vault`);
      setVault(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load documents'); }
    finally { setLoading(false); }
  }

  const onUploadMisc = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const formData = new FormData();
    formData.append('file', f);
    formData.append('name', f.name);
    formData.append('category', 'personal');
    if (!isSelf) formData.append('target_user_id', employeeId);
    setUploading(true);
    api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => { toast.success('Uploaded'); load(); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || 'Upload failed'))
      .finally(() => { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; });
  };

  const deleteMisc = async (id) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openLetter = async (url) => {
    try { await previewPDF(`${process.env.REACT_APP_BACKEND_URL}${url}`); }
    catch (e) { toast.error('Preview failed: ' + (e.message || 'unknown')); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-6">Loading documents…</div>;
  if (!vault) return null;

  const totalCount = Object.values(vault.counts || {}).reduce((s, n) => s + n, 0);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="documents-vault">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
          <FolderOpen className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Documents vault</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">
            {isSelf ? 'My documents' : `${vault.employee.name}'s documents`}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {totalCount} {totalCount === 1 ? 'file' : 'files'} across 5 folders — synced both ways between this view and {isSelf ? "HR's view" : "the employee's own profile"}.
          </p>
        </div>
      </div>

      {Object.entries(FOLDER_META).map(([key, meta]) => {
        const items = vault[key] || [];
        const Icon = meta.icon;
        const isOpen = open[key];
        return (
          <div key={key} data-testid={`folder-${key}`}
               className="mb-4 rounded-2xl border-2 border-slate-300 bg-slate-50/40 hover:border-[#3D4A8E]/40 hover:shadow-sm transition-all">
            {/* Folder header */}
            <button onClick={() => setOpen({ ...open, [key]: !isOpen })}
                    className="w-full flex items-center justify-between p-4 text-left bg-white rounded-t-2xl"
                    data-testid={`folder-toggle-${key}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <Icon className={`w-4 h-4 ${meta.iconClass}`} strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                    {meta.label}
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {items.length}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{meta.help}</div>
                </div>
              </div>
              {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0 text-slate-400" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 text-slate-400" />}
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-2 border-t-2 border-slate-200 pt-3 bg-slate-50/40">
                {items.length === 0 ? (
                  <div className="text-xs italic text-slate-400 px-2 pb-1">No documents in this folder yet.</div>
                ) : (
                  items.map((it, i) => renderItem(key, it, i, openLetter, deleteMisc, isSelf, canManage))
                )}
                {/* Upload button for miscellaneous — both HR and self can add */}
                {key === 'miscellaneous' && (canManage || isSelf) && (
                  <div className="pt-2">
                    <input ref={fileInputRef} type="file" className="hidden" onChange={onUploadMisc} data-testid="misc-upload-input" />
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                            data-testid="misc-upload-btn" className="rounded-full h-8">
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? 'Uploading…' : 'Upload to miscellaneous'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderItem(folderKey, it, idx, openLetter, deleteMisc, isSelf, canManage) {
  // Employment letters — synthetic, opened via PDF endpoint
  if (folderKey === 'employment_letters') {
    return (
      <div key={`${it.type}-${idx}`} data-testid={`letter-${it.type}`} className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 flex-shrink-0 text-slate-400" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{it.label}</div>
            <div className="text-[11px] text-slate-500">Issued {fmt(it.issued_at)}</div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => openLetter(it.url)} className="rounded-full h-7 text-xs">
          <Eye className="w-3 h-3 mr-1" /> Open
        </Button>
      </div>
    );
  }
  // Reimbursements — show invoice list
  if (folderKey === 'reimbursements') {
    return (
      <div key={it.id} className="bg-white rounded-lg border border-slate-200 px-3 py-2 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900 truncate">{it.title} <span className="text-[11px] text-slate-500 font-normal">· ₹{Number(it.amount || 0).toLocaleString('en-IN')} · {it.status}</span></div>
        </div>
        {it.files.map(f => (
          <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 text-xs text-slate-600 hover:text-[#3D4A8E] hover:underline pl-2">
            <span className="truncate"><FileText className="w-3 h-3 inline mr-1" /> {f.filename}</span>
            <Download className="w-3 h-3 flex-shrink-0" />
          </a>
        ))}
      </div>
    );
  }
  // BGC, Certifications, Miscellaneous — generic file row
  const file_id = it.file_id;
  const label = it.label || it.title || it.filename || '—';
  return (
    <div key={it.id || file_id || idx} className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-2 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 flex-shrink-0 text-slate-400" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {it.filename && it.filename !== label ? it.filename + ' · ' : ''}{fmt(it.uploaded_at || it.created_at || it.issued_on)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {file_id && (
          <a href={fileUrl(file_id)} target="_blank" rel="noreferrer"
             data-testid={`open-${folderKey}-${idx}`}
             className="text-xs text-slate-600 hover:text-[#3D4A8E] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white">
            <Eye className="w-3 h-3" /> Open
          </a>
        )}
        {folderKey === 'miscellaneous' && (canManage || isSelf) && it.id && (
          <button onClick={() => deleteMisc(it.id)} data-testid={`delete-${folderKey}-${idx}`}
                  className="text-xs text-red-600 hover:bg-red-100 inline-flex items-center gap-1 px-2 py-1 rounded-full">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
